import type { SeatDiagramLabelContext } from "../../../constants/seat-diagram-schematic-labels"
import type { PdmAssetRecord } from "../../../types"
import {
  extractFactoryMatchAttributes,
  formatSeatDiagramSchematicLabel,
  resolveFactoryMatchKitDisplayValue,
  unwrapValue,
  FACTORY_MATCH_PATTERN_DEFAULT,
  FACTORY_MATCH_TONE_DEFAULT,
} from "../../../pdm/normalizePdm"

/**
 * PDM digital-assets image URLs are disabled for now — Factory Match uses Dato only.
 * Uncomment the body below to restore `/api/pdm-images/...` fallbacks.
 */
export function buildPdmImageUrl(assetDetails: {
  imageFilename: string
  imageHash: string
}): string {
  void assetDetails
  return ""
  // if (assetDetails.imageHash) {
  //   return `/api/pdm-images/${assetDetails.imageHash}`
  // }
  // if (assetDetails.imageFilename) {
  //   return `/api/pdm-images/${assetDetails.imageFilename}`
  // }
  // return ""
}

export function resolveFactoryMatchOptionValue(
  assetType: string,
  attrs: ReturnType<typeof extractFactoryMatchAttributes>,
  product: Record<string, unknown>,
  application: Record<string, unknown>,
  partNumber: string,
  basePartNumber: string,
  rowLabelHint = "",
  labelContext?: SeatDiagramLabelContext,
  perforationOverride?: string | null
): string {
  const productTitle = unwrapValue(product.title ?? product.Title)
  const kitDisplay = resolveFactoryMatchKitDisplayValue(
    partNumber,
    basePartNumber
  )

  switch (assetType.toUpperCase()) {
    case "KT":
      return kitDisplay || productTitle
    case "FP":
    case "PT":
      return attrs.pattern || productTitle || FACTORY_MATCH_PATTERN_DEFAULT
    case "TN":
      return attrs.tone || productTitle || FACTORY_MATCH_TONE_DEFAULT
    case "PF":
    case "PL":
      if (perforationOverride === null) {
        return ""
      }
      return (
        attrs.perforation || productTitle || (perforationOverride?.trim() ?? "")
      )
    case "PC":
      return attrs.primaryColor || productTitle
    case "IC":
      return attrs.insertColor || productTitle
    case "ST":
      return attrs.stitch || productTitle
    case "SD":
    case "FED":
    case "FD":
      return formatSeatDiagramSchematicLabel(
        application,
        productTitle,
        product,
        rowLabelHint,
        labelContext
      )
    default:
      return productTitle || kitDisplay
  }
}

/**
 * Get the raw untransformed value from PDM for Dato matching.
 * This returns the original PDM attribute value without any display formatting.
 */
export function resolveFactoryMatchOptionRawValue(
  assetType: string,
  attrs: ReturnType<typeof extractFactoryMatchAttributes>,
  product: Record<string, unknown>,
  application: Record<string, unknown>,
  partNumber: string,
  basePartNumber: string,
  rowLabelHint = "",
  labelContext?: SeatDiagramLabelContext,
  perforationOverride?: string | null
): string {
  const productTitle = unwrapValue(product.title ?? product.Title)
  const kitDisplay = resolveFactoryMatchKitDisplayValue(
    partNumber,
    basePartNumber
  )

  // Return raw PDM attribute values without transformation
  switch (assetType.toUpperCase()) {
    case "KT":
      return attrs.kit || kitDisplay || productTitle
    case "FP":
    case "PT":
      return attrs.pattern || productTitle || FACTORY_MATCH_PATTERN_DEFAULT
    case "TN":
      return attrs.tone || productTitle || FACTORY_MATCH_TONE_DEFAULT
    case "PF":
    case "PL":
      if (perforationOverride === null) {
        return ""
      }
      return (
        attrs.perforation || productTitle || (perforationOverride?.trim() ?? "")
      )
    case "PC":
      return attrs.primaryColor || productTitle // Raw value without transformation
    case "IC":
      return attrs.insertColor || productTitle // Raw value without transformation
    case "ST":
      return attrs.stitch || productTitle // Raw value without transformation
    case "SD":
    case "FED":
    case "FD":
      return formatSeatDiagramSchematicLabel(
        application,
        productTitle,
        product,
        rowLabelHint,
        labelContext
      )
    default:
      return productTitle || kitDisplay
  }
}
export type DatoImageLookupResult = {
  src: string | null
  matchReason: string | null
  matchedRow: { title: string; value: string } | null
}

export function getDatoRowMatchReason(
  datoTitle: string,
  datoValue: string,
  normalizedTitle: string,
  normalizedValue: string
): string | null {
  if (datoValue === normalizedValue) return "exact value"
  if (datoTitle === normalizedTitle) return "exact title"
  if (datoValue && normalizedValue && datoValue.includes(normalizedValue)) {
    return "partial value (dato contains option)"
  }
  if (datoValue && normalizedValue && normalizedValue.includes(datoValue)) {
    return "partial value (option contains dato)"
  }
  if (datoTitle && normalizedTitle && datoTitle.includes(normalizedTitle)) {
    return "partial title (dato contains option)"
  }
  if (datoTitle && normalizedTitle && normalizedTitle.includes(datoTitle)) {
    return "partial title (option contains dato)"
  }
  return null
}

/** True when PDM digital_assets[0] is a seat diagram / schematic asset. */
export function isPdmSchematicDiagramDigitalAsset(assetDetails: {
  imageFilename: string
  assetType: string
}): boolean {
  const code = assetDetails.assetType.trim().toUpperCase()
  if (["SD", "FED", "FD"].includes(code)) return true
  const lowerFile = assetDetails.imageFilename.toLowerCase()
  return lowerFile.includes("-sd") || lowerFile.includes("_sd")
}

/**
 * A Factory Match CMS row is a seat drawing when its key is "schematic"
 * and its value is a full SKU: longer than this product's base part number.
 * Short values like TOY2409E are not.
 */
export function isSchematicDatoRowValue(
  rowValue: string,
  basePartNumber: string,
  rowKey = ""
): boolean {
  if (rowKey.trim().toLowerCase() !== "schematic") return false
  const value = rowValue.trim()
  const base = basePartNumber.trim()
  if (!value || !base) return false
  return value.length > base.length
}

/**
 * Dato row.value to match against.
 * Schematic assets: the full part number (SKU).
 * All other options: rawValue from PDM attributes.
 */
export function resolveDatoMatchValueForFactoryMatchOption(
  assetDetails: { imageFilename: string; assetType: string },
  optionRawValue: string,
  partNumber = ""
): { matchValue: string; matchStrategy: "rawValue" | "sku" } {
  if (isPdmSchematicDiagramDigitalAsset(assetDetails)) {
    return { matchValue: partNumber.trim(), matchStrategy: "sku" }
  }
  return { matchValue: optionRawValue.trim(), matchStrategy: "rawValue" }
}

export function lookupDatoFactoryMatchImage(
  factoryMatchDatoAsset: PdmAssetRecord | null,
  optionTitle: string,
  optionValue: string
): DatoImageLookupResult {
  if (!factoryMatchDatoAsset?.rows?.length) {
    return { src: null, matchReason: null, matchedRow: null }
  }

  const normalizedTitle = optionTitle.trim().toLowerCase()
  const normalizedValue = optionValue.trim().toLowerCase()

  for (const row of factoryMatchDatoAsset.rows) {
    const datoTitle = row.title?.trim().toLowerCase() ?? ""
    const datoValue = row.value?.trim().toLowerCase() ?? ""
    const matchReason = getDatoRowMatchReason(
      datoTitle,
      datoValue,
      normalizedTitle,
      normalizedValue
    )

    if (!matchReason) continue

    const src = row.image?.responsiveImage?.src ?? null
    return {
      src,
      matchReason,
      matchedRow: {
        title: row.title?.trim() ?? "",
        value: row.value?.trim() ?? "",
      },
    }
  }

  return { src: null, matchReason: null, matchedRow: null }
}

/**
 * Seat drawing only. The CMS row key must be "schematic", the value must be
 * longer than the base part number, and the value must exactly equal the full SKU.
 */
export function lookupDatoSchematicImageBySku(
  factoryMatchDatoAsset: PdmAssetRecord | null,
  sku: string,
  basePartNumber: string
): DatoImageLookupResult {
  if (!factoryMatchDatoAsset?.rows?.length) {
    return { src: null, matchReason: null, matchedRow: null }
  }

  const normalizedSku = sku.trim().toLowerCase()
  if (!normalizedSku) {
    return { src: null, matchReason: null, matchedRow: null }
  }

  for (const row of factoryMatchDatoAsset.rows) {
    const datoValue = row.value?.trim() ?? ""
    if (!isSchematicDatoRowValue(datoValue, basePartNumber, row.key ?? "")) {
      continue
    }
    if (datoValue.toLowerCase() !== normalizedSku) continue

    const src = row.image?.responsiveImage?.src ?? null
    return {
      src,
      matchReason: "exact sku",
      matchedRow: {
        title: row.title?.trim() ?? "",
        value: datoValue,
      },
    }
  }

  return { src: null, matchReason: null, matchedRow: null }
}
